/**
 * Transform a Figma share/proto URL into the embed URL the iframe expects.
 *
 * Mirrors FolioForm.js editUrl() so Aero UT and Figmafolio produce the same
 * embed URL from the same input. Differences:
 *   - Omits FolioForm's broken `else` branch that zeroed newUrl when the URL
 *     had both `scaling=contain` and `content-scaling=responsive`.
 *   - Appends `&scaling=scale-down-width` if the input has no `scaling=` param
 *     at all, so prototypes fill the iframe width instead of Figma's default
 *     `contain`.
 *
 * Idempotent: already-embedded URLs are detected by the embed-host marker and
 * returned with only the trailing `&hide-ui=1&hotspot-hints=0` / scaling
 * adjustments applied, so calling this on an already-transformed URL is safe.
 */
export function editFigmaUrl(url) {
  if (!url || url === '') return '';
  url = cleanFigmaLink(url);

  const wordToRemove = 'https://';
  const hideUi = '&hide-ui=1';
  const hotspot = '&hotspot-hints=0';
  const embedHost = 'www.figma.com/embed?embed_host=share&url=https%3A%2F%2F';

  let modifiedString = url.replace(new RegExp(`\\b${wordToRemove}\\b`, 'gi'), '');
  if (url.includes('content-scaling=responsive')) {
    modifiedString = encodeURIComponent(modifiedString);
  }

  let newUrl = modifiedString.includes(embedHost)
    ? url
    : 'https://' + embedHost + modifiedString;

  if (!newUrl.includes(hideUi)) newUrl += hideUi;
  if (!newUrl.includes(hotspot)) newUrl += hotspot;

  if (newUrl.includes('scaling=contain')) {
    if (!newUrl.includes('content-scaling=responsive')) {
      newUrl = newUrl.replace(/scaling=contain/g, 'scaling=scale-down-width');
    }
  } else if (newUrl.includes('scaling=min-zoom')) {
    newUrl = newUrl.replace(/scaling=min-zoom/g, 'scaling=scale-down-width');
  } else if (newUrl.includes('scaling=scale-down') && !newUrl.includes('scaling=scale-down-width')) {
    newUrl = newUrl.replace(/scaling=scale-down/g, 'scaling=scale-down-width');
  }

  // Fallback: if the input carried no scaling= param at all, Figma's embed
  // defaults to `contain` which leaves visible letterboxing. Opt into the same
  // fill-width behaviour FolioForm's rewrites produce for every other case.
  if (!/[?&]scaling=/.test(newUrl)) {
    newUrl += '&scaling=scale-down-width';
  }

  return newUrl;
}

// ---------------------------------------------------------------------------
// Link clean-up. Figmafolio's form gate keeps most bad links out; Aero's
// wizard, test path and older saves did not, so a pasted /design, /file or
// embed.figma.com link was wrapped in the embed host as-is and Figma answered
// "invalid URL". cleanFigmaLink turns those into the plain /proto/ share link
// that editFigmaUrl expects. It never touches an already-wrapped good link.
// ---------------------------------------------------------------------------
const WRAPPED = /figma\.com\/embed\?/i;
const DROP_PARAMS = ['embed_host', 'embed-host', 'embed-origin', 'url', 'hide-ui', 'hotspot-hints'];

function toProtoLink(raw) {
  let u;
  try {
    u = new URL(/^https?:\/\//i.test(raw) ? raw : 'https://' + raw);
  } catch (e) {
    return raw;
  }
  const host = u.hostname.toLowerCase();
  if (host !== 'figma.com' && !host.endsWith('.figma.com')) return raw;
  const parts = u.pathname.split('/').filter(Boolean);
  const kind = parts[0];
  if (!['proto', 'design', 'file', 'embed'].includes(kind) || parts.length < 2) return raw;
  let rest = parts.slice(1);
  // /design/:key/branch/:branchKey/:name -> /proto/:branchKey/:name
  if (rest[1] === 'branch' && rest[2]) rest = [rest[2], ...rest.slice(3)];
  DROP_PARAMS.forEach((k) => u.searchParams.delete(k));
  const node = u.searchParams.get('node-id');
  if (kind !== 'proto' && node && !u.searchParams.get('starting-point-node-id')) {
    u.searchParams.set('starting-point-node-id', node);
  }
  const qs = u.searchParams.toString().replace(/%3A/gi, ':');
  return 'https://www.figma.com/proto/' + rest.join('/') + (qs ? '?' + qs : '');
}

export function cleanFigmaLink(raw) {
  const url = (raw || '').trim();
  if (!url) return '';
  if (WRAPPED.test(url)) {
    let inner = null;
    let extras = '';
    try {
      const q = new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url).searchParams;
      inner = q.get('url');
      const keep = new URLSearchParams();
      q.forEach((v, k) => { if (!DROP_PARAMS.includes(k)) keep.append(k, v); });
      extras = keep.toString();
    } catch (e) { /* fall through */ }
    if (!inner) return url;
    if (/^(https?:\/\/)?(www\.)?figma\.com\/proto\//i.test(inner)) return url; // already good
    const fixed = toProtoLink(inner);
    return extras ? fixed + (fixed.includes('?') ? '&' : '?') + extras : fixed;
  }
  return toProtoLink(url);
}

// View time: make an already-saved embed URL safe to load. Idempotent, and
// returns good links byte-for-byte unchanged.
export function ensureFigmaEmbedSrc(src) {
  if (!src || !/figma\.com/i.test(src)) return src;
  const cleaned = cleanFigmaLink(src);
  if (cleaned === src.trim() && WRAPPED.test(src)) return src;
  return editFigmaUrl(src);
}
